import { Button } from "@/components/ui/button";
import { ChevronDown, LogOut, Menu, Shield } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { User } from "@supabase/supabase-js";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import logoImage from "@/assets/newlogo.png";
import { CheckCircle, Crown, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link, useNavigate } from "react-router-dom";
import { categories } from "@/components/CategoriesSection";

interface NavbarProps {
  onSearchClick: () => void;
}

export function Navbar({ onSearchClick }: NavbarProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { toast } = useToast();
  const { hasAccess, hasPaid, planName, daysRemaining, loading: paymentLoading } = usePaymentStatus();
  const navigate = useNavigate();

  const isPro = planName === "Pro Plan";
  const showDays = typeof daysRemaining === "number" && planName !== "Pro Plan";
  const daysLabel = showDays
    ? ` • ${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left`
    : "";

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);

      // Check admin status if user is logged in
      if (session?.user) {
        checkAdminStatus(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);

      // Check admin status on sign in
      if (event === "SIGNED_IN" && session?.user) {
        checkAdminStatus(session.user.id);
      } else if (event === "SIGNED_OUT") {
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [toast]);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase.rpc("is_admin", { user_id: userId });
      if (!error) {
        setIsAdmin(data || false);
      }
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Clear payment status from localStorage on logout
      localStorage.removeItem("payment_completed");
      localStorage.removeItem("payment_plan");
      localStorage.removeItem("payment_amount");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleProtectedClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();

    // Check localStorage first for immediate access after payment
    const localPaymentCompleted = localStorage.getItem("payment_completed") === "true";

    // If payment is loading, don't block access
    if (paymentLoading) {
      navigate(href);
      return;
    }

    // Check both hook status and localStorage
    if (!hasAccess && !localPaymentCompleted) {
      // Scroll to pricing section instead of showing toast
      const pricingSection = document.getElementById("pricing");
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate('/', { replace: true });
        setTimeout(() => {
          const pricingSection = document.getElementById('pricing');
          pricingSection?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
      return;
    }

    navigate(href);
  };

  const scrollToN8nTemplates = () => {
    const goToSection = () => {
      const section = document.getElementById("n8n-templates");
      section?.scrollIntoView({ behavior: "smooth" });
    };

    if (document.getElementById("n8n-templates")) {
      goToSection();
    } else {
      navigate("/");
      setTimeout(goToSection, 200);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src={logoImage}
                alt="AI Auto Base"
                width="136"
                height="40"
                // @ts-expect-error fetchpriority is valid HTML attribute
                fetchpriority="high"
                className="h-8 sm:h-10 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <div className="relative group">
              <div className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer flex items-center gap-1">
                Resources
                <ChevronDown className="w-4 h-4" />
              </div>
              <div className="absolute top-full left-0 w-56 bg-popover border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  {["Claude Code", "AntiGravity", "Open Claw", "n8n", "Other"].map((cat) => (
                    <button
                      key={cat}
                      onClick={() => {
                        const goto = () => {
                          const el = document.getElementById('resources-vault');
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth' });
                            window.dispatchEvent(new CustomEvent('resources-vault:set-category', { detail: cat }));
                          }
                        };
                        if (document.getElementById('resources-vault')) {
                          goto();
                        } else {
                          navigate('/');
                          setTimeout(goto, 250);
                        }
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                    >
                      {cat}
                    </button>
                  ))}
                  {/* Prompts navega a su propia página; los de arriba filtran
                      la sección del home. El separador marca esa diferencia. */}
                  <div className="my-2 border-t border-border" />
                  <button
                    onClick={(e) => handleProtectedClick(e, "/prompts")}
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Prompt Library
                  </button>
                  {/* Build Packs is public (the files inside are gated), so a plain link. */}
                  <Link
                    to="/packs"
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Build Packs
                  </Link>
                  {/* Classroom Library: public index of the Skool classroom lessons (bodies/files gated). */}
                  <Link
                    to="/classroom"
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Classroom Library
                  </Link>
                  {/* Community Resource: the Skool community category of the same name, mirrored daily
                      and split by month (bodies/images/files gated). The emoji is part of the name on Skool. */}
                  <Link
                    to="/community-resources"
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Community Resource 📚
                  </Link>
                  <Link
                    to="/newsletter"
                    className="block w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Newsletter
                  </Link>
                </div>
              </div>
            </div>
            <div
              onClick={(e) => handleProtectedClick(e, "/courses")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              Courses
            </div>
            <div className="relative group">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-sm text-muted-foreground hover:text-foreground hover:bg-[#f15000] transition-colors"
              >
                AI Growth Kit
                <ChevronDown className="w-4 h-4" />
              </Button>
              <div className="absolute top-full left-0 w-56 bg-popover border border-border rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <Link
                    to="/ai-automation-roadmap"
                    onClick={(e) => handleProtectedClick(e, "/ai-automation-roadmap")}
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    AI Automation Roadmap
                  </Link>
                  <Link
                    to="/ai-agency-roadmap"
                    onClick={(e) => handleProtectedClick(e, "/ai-agency-roadmap")}
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    AI Agency Roadmap
                  </Link>
                  <Link
                    to="/automation-money-making"
                    onClick={(e) => handleProtectedClick(e, "/automation-money-making")}
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Automation Money Making
                  </Link>
                  <Link
                    to="/build-free-ai-agency-website"
                    onClick={(e) => handleProtectedClick(e, "/build-free-ai-agency-website")}
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Build Free AI Agency Website
                  </Link>
                  <Link
                    to="/launch-your-ai-agency"
                    onClick={(e) => handleProtectedClick(e, "/launch-your-ai-agency")}
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Launch Your AI Agency
                  </Link>
                  <Link
                    to="/best-ai-automation-tools"
                    onClick={(e) => handleProtectedClick(e, "/best-ai-automation-tools")}
                    className="block px-4 py-2 text-sm text-popover-foreground hover:bg-[#f15000] hover:text-white transition-colors cursor-pointer"
                  >
                    Best AI Automation Tools
                  </Link>
                </div>
              </div>
            </div>
            <Link to="/videos" className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
              Videos
            </Link>
            <Link to="/apps" className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
              Apps
            </Link>
            <a
              href="https://aiautobase.com/#pricing"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              Pricing
            </a>
            {isAdmin && (
              <Link
                to="/admin"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer flex items-center gap-1"
              >
                <Shield className="w-4 h-4" />
                Admin
              </Link>
            )}
            <button
              type="button"
              onClick={scrollToN8nTemplates}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              n8n Templates
            </button>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
            {loading ? (
              <div className="w-16 sm:w-20 h-8 bg-muted animate-pulse rounded" />
            ) : user ? (
              <>
                {/* Payment Status Badge - Desktop */}
                {hasPaid && (
                  <Badge 
                    className="bg-gradient-to-br from-primary via-primary to-primary/90 text-white border-0 px-3 py-1.5 flex items-center gap-2 text-xs hidden lg:flex shadow-[0_8px_16px_-4px_rgba(241,80,0,0.4),0_4px_8px_-2px_rgba(241,80,0,0.3),inset_0_2px_4px_0_rgba(255,255,255,0.3),inset_0_-2px_8px_0_rgba(0,0,0,0.2)] hover:shadow-[0_12px_20px_-4px_rgba(241,80,0,0.5),0_6px_10px_-2px_rgba(241,80,0,0.4),inset_0_2px_4px_0_rgba(255,255,255,0.3),inset_0_-2px_8px_0_rgba(0,0,0,0.2)] transition-all duration-300 backdrop-blur-sm relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:to-transparent before:rounded-md before:pointer-events-none after:absolute after:inset-0 after:bg-gradient-to-t after:from-black/10 after:to-transparent after:rounded-md after:pointer-events-none border-t-2 border-white/30"
                  >
                    {isPro ? (
                      <Crown className="w-3.5 h-3.5" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5" />
                    )}
                    <CheckCircle className="w-3.5 h-3.5" />
                    <span className="font-medium whitespace-nowrap">{planName}{daysLabel} - Full Access</span>
                  </Badge>
                )}
                
                {/* Payment Status Badge - Mobile */}
                {hasPaid && (
                  <Badge 
                    className="bg-gradient-to-br from-primary via-primary to-primary/90 text-white border-0 px-2 py-1 flex items-center gap-1.5 text-[10px] lg:hidden shadow-[0_6px_12px_-3px_rgba(241,80,0,0.4),0_3px_6px_-2px_rgba(241,80,0,0.3),inset_0_1px_3px_0_rgba(255,255,255,0.3),inset_0_-1px_6px_0_rgba(0,0,0,0.2)] backdrop-blur-sm relative before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:to-transparent before:rounded-md before:pointer-events-none after:absolute after:inset-0 after:bg-gradient-to-t after:from-black/10 after:to-transparent after:rounded-md after:pointer-events-none border-t border-white/30"
                  >
                    {isPro ? (
                      <Crown className="w-3 h-3" />
                    ) : (
                      <Sparkles className="w-3 h-3" />
                    )}
                    <CheckCircle className="w-3 h-3" />
                    <span className="font-medium whitespace-nowrap">{planName}{daysLabel} - Full Access</span>
                  </Badge>
                )}
                
                <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2 text-sm hidden lg:flex shadow-[0_4px_8px_-2px_rgba(0,0,0,0.15),inset_0_1px_2px_0_rgba(255,255,255,0.2)] hover:shadow-[0_6px_12px_-2px_rgba(0,0,0,0.2),inset_0_1px_2px_0_rgba(255,255,255,0.2)] active:shadow-[0_2px_4px_-1px_rgba(0,0,0,0.15),inset_0_1px_2px_0_rgba(255,255,255,0.2)] hover:scale-105 active:scale-95 transition-all duration-200">
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Link to="/auth" className="hidden sm:block">
                  <Button variant="ghost" size="sm" className="text-sm shadow-[0_4px_8px_-2px_rgba(0,0,0,0.15),inset_0_1px_2px_0_rgba(255,255,255,0.2)] hover:shadow-[0_6px_12px_-2px_rgba(0,0,0,0.2),inset_0_1px_2px_0_rgba(255,255,255,0.2)] active:shadow-[0_2px_4px_-1px_rgba(0,0,0,0.15),inset_0_1px_2px_0_rgba(255,255,255,0.2)] hover:scale-105 active:scale-95 transition-all duration-200">
                    Sign In
                  </Button>
                </Link>
                <Link to="/auth" className="hidden sm:block">
                  <Button size="sm" className="text-sm">
                    Get Started
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="lg:hidden p-2">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="text-left">Menu</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col gap-4 mt-8">
                  {/* n8n Templates */}
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      scrollToN8nTemplates();
                    }}
                  >
                    n8n Templates
                  </Button>

                  {/* Navigation Links */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate('/');
                        setTimeout(() => {
                          const el = document.getElementById('resources-vault');
                          el?.scrollIntoView({ behavior: 'smooth' });
                        }, 200);
                      }}
                    >
                      Resources
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        handleProtectedClick(e as any, "/prompts");
                      }}
                    >
                      Prompt Library
                    </Button>
                    <Link to="/packs" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        Build Packs
                      </Button>
                    </Link>
                    <Link to="/classroom" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        Classroom Library
                      </Button>
                    </Link>
                    <Link to="/community-resources" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        Community Resource 📚
                      </Button>
                    </Link>
                    <Link to="/videos" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        Videos
                      </Button>
                    </Link>
                    <Link to="/apps" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        Apps
                      </Button>
                    </Link>
                    <Link to="/newsletter" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start">
                        Newsletter
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        handleProtectedClick(e as any, "/courses");
                      }}
                    >
                      Courses
                    </Button>

                    {/* AI Growth Kit Accordion */}
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="ai-kit" className="border-none">
                        <AccordionTrigger className="py-2 px-4 hover:no-underline hover:bg-accent rounded-md">
                          AI Growth Kit
                        </AccordionTrigger>
                        <AccordionContent className="pl-4">
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-sm"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                handleProtectedClick(e as any, "/ai-automation-roadmap");
                              }}
                            >
                              AI Automation Roadmap
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-sm"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                handleProtectedClick(e as any, "/ai-agency-roadmap");
                              }}
                            >
                              AI Agency Roadmap
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-sm"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                handleProtectedClick(e as any, "/automation-money-making");
                              }}
                            >
                              Automation Money Making
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-sm"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                handleProtectedClick(e as any, "/build-free-ai-agency-website");
                              }}
                            >
                              Build Free AI Agency Website
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-sm"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                handleProtectedClick(e as any, "/launch-your-ai-agency");
                              }}
                            >
                              Launch Your AI Agency
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-sm"
                              onClick={(e) => {
                                setMobileMenuOpen(false);
                                handleProtectedClick(e as any, "/best-ai-automation-tools");
                              }}
                            >
                              Best AI Automation Tools
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                      onClick={() => {
                        setMobileMenuOpen(false);
                        navigate('/', { replace: true });
                        setTimeout(() => {
                          const pricingSection = document.getElementById('pricing');
                          pricingSection?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                    >
                      Pricing
                    </Button>

                    {isAdmin && (
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-2"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          navigate("/admin");
                        }}
                      >
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </Button>
                    )}
                  </div>

                  {/* Auth Actions */}
                  <div className="flex flex-col gap-2 mt-4 pt-4 border-t">
                    {user ? (
                      <Button
                        variant="outline"
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="w-full justify-start gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </Button>
                    ) : (
                      <>
                        <Link to="/auth" className="w-full">
                          <Button variant="outline" className="w-full">
                            Sign In
                          </Button>
                        </Link>
                        <Link to="/auth" className="w-full">
                          <Button className="w-full bg-gradient-primary hover:opacity-90 transition-opacity">
                            Get Started
                          </Button>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
